import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import React, { useEffect, useRef } from "react";

type Props = {
  label?: string;
  value: string | null | undefined;
  onChange: (html: string) => void;
  disabled?: boolean;
  minHeight?: number;
  helperText?: string;
};

/**
 * The onboarding's rich-text control (S6.7, story 5613): exactly bold, italic, bulleted and
 * numbered list — nothing else, so no link, image, colour or font can enter the payload. Paste
 * inserts plain text only.
 *
 * The editor writes into an uncontrolled contentEditable and only takes `value` from outside when
 * it does not hold focus, which is what keeps the caret from jumping while typing. The stored HTML
 * is filtered to the allow-list server-side on every write and again on render — this control is a
 * convenience, never the security boundary.
 */
const RichTextEditor: React.FC<Props> = ({
  label,
  value,
  onChange,
  disabled,
  minHeight = 96,
  helperText,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    // Never overwrite what the user is typing; only sync when the caret is elsewhere.
    if (document.activeElement === editor) return;
    const incoming = value || "";
    if (editor.innerHTML !== incoming) editor.innerHTML = incoming;
  }, [value]);

  const exec = (command: string) => {
    if (disabled) return;
    // Only focus if the editor does not already hold it — a toolbar click when the editor was
    // never focused at all (nothing to preserve) still needs this to make execCommand apply to it.
    // When it does already hold focus, preserveSelection below is what kept it there, and calling
    // focus() again here is exactly what threw the caret back to the start of the block (bug 5856).
    if (document.activeElement !== editorRef.current) editorRef.current?.focus();
    document.execCommand(command, false);
    onChange(editorRef.current?.innerHTML || "");
  };

  /**
   * A toolbar button's mousedown otherwise steals focus from the editor before its click handler
   * runs, which collapses the browser's selection — so by the time execCommand ran, there was no
   * caret left at the end of "Booking Details" to apply the list at, and the browser fell back to
   * the start of the block (bug 5856). Suppressing the default here keeps the editor focused and
   * the selection exactly where the user left it.
   */
  const preserveSelection = (event: React.MouseEvent) => event.preventDefault();

  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    // Paste inserts plain text only (S6.7).
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            p: 0.5,
          }}
        >
          <Tooltip title="Bold">
            <IconButton
              size="small"
              disabled={disabled}
              onMouseDown={preserveSelection}
              onClick={() => exec("bold")}
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton
              size="small"
              disabled={disabled}
              onMouseDown={preserveSelection}
              onClick={() => exec("italic")}
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bulleted list">
            <IconButton
              size="small"
              disabled={disabled}
              onMouseDown={preserveSelection}
              onClick={() => exec("insertUnorderedList")}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered list">
            <IconButton
              size="small"
              disabled={disabled}
              onMouseDown={preserveSelection}
              onClick={() => exec("insertOrderedList")}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={() => onChange(editorRef.current?.innerHTML || "")}
          onBlur={() => onChange(editorRef.current?.innerHTML || "")}
          onPaste={onPaste}
          sx={{
            minHeight,
            p: 1,
            fontSize: 14,
            outline: "none",
            "& p": { margin: 0 },
            "& ul, & ol": { margin: 0, paddingLeft: 3 },
          }}
        />
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RichTextEditor;
